import { Component, EventEmitter, HostBinding, ViewEncapsulation, Input, Output, QueryList, TemplateRef, ViewChildren, OnInit, ViewChild } from '@angular/core';
import { SelectionChangedEvent } from '../../../../node_modules/ag-grid-community';
import { DragDropData } from '../../models/drag-drop-data';
import { NgForm } from '@angular/forms';
import { GenericColDef, GridContext, GridRowSelectionType } from '../../models/grid-context';
import { GenericGridComponent } from '../generic-grid/generic-grid.component';
import { GenericTreeNode } from '../generic-tree/generic-tree-node';
import { GenericTreeComponent } from '../generic-tree/generic-tree.component';
import { DrRequestInfo } from '../../models/dr-request-info';
import * as _ from 'underscore';
import { isNumber } from '../../models/utils';
import * as _lo from 'lodash';
import { GroupFieldMode } from '../../models/group-field';

@Component({
    standalone: false,
    selector: 'res-left-right-selection-box',
    templateUrl: './left-right-selection-box.component.html',
    styleUrls: ['./left-right-selection-box.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class LeftRightSelectionBoxComponent implements OnInit {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild('leftRightSelectionBoxForm', { static: true }) leftRightSelectionBoxForm: NgForm;
    @ViewChildren(GenericGridComponent) genericGrids: QueryList<GenericGridComponent>;
    @ViewChildren(GenericTreeComponent) genericTrees: QueryList<GenericTreeComponent>;

    // trees or normal lists with drag and drop
    @Input() useTrees = false;

    // grids use drag and drop
    @Input() useDragAndDrop = true;
    @Input() showLeftRightButton = true;
    @Input() adjustHeaderCheckboxStyles = false

    @Input() automationId = 'left-right-list';
    automationIdLeft = 'left-list';
    automationIdRight = 'right-list';
    @Input() showRightHeaderContextMenu = true;

    // item arrays for both lists/trees
    @Input() itemsLeft: Array<any>;
    @Input() itemsRight: Array<any>;

    // list titles
    @Input() leftListTitle: string;
    @Input() rightListTitle: string;
    @Input() leftListRequired: boolean;
    @Input() rightListRequired: boolean;
    @Input() rightListRequiredTooltip: string;

    // grid info
    @Input() leftListSelection: GridRowSelectionType = GridRowSelectionType.multipleCheckboxWithRowClickSelection;
    @Input() rightListSelection: GridRowSelectionType = GridRowSelectionType.multipleCheckboxWithRowClickSelection;

    @Input() leftListGridContext: GridContext;
    @Input() rightListGridContext: GridContext;

    @Input() leftListPagerVisible = false;
    @Input() leftListPagerName = '';
    @Input() leftListPagerTemplate: TemplateRef<any> = null;

    @Input() leftListColumns: Array<GenericColDef>;
    @Input() rightListColumns: Array<GenericColDef>;

    @Input() leftListHeaderHeight: number;
    @Input() rightListHeaderHeight: number;
    @Input() leftListHeight: number;
    @Input() rightListHeight: number;

    @Input() leftListRowHeightCallback: (params: any) => number;
    @Input() rightListRowHeightCallback: (params: any) => number;

    @Input() leftListRequestInfo: DrRequestInfo;
    @Input() leftListResponseArrayCallback: (response: any) => void;
    @Input() leftListResponseCallback: (response: any) => void;

    // templates for other buttons above the grid
    @Input() leftListTemplate: TemplateRef<any>;
    @Input() rightListTemplate: TemplateRef<any>;
    @Input() rightListBottomTemplate: TemplateRef<any>;

    // custom size in percentage for left list, right list will match the rest up to 100%
    @Input() leftListSize = 50;

    // flag to automatically sort items on init and on drag
    @Input() sortItemsLeft = false;
    @Input() sortItemsRight = false;

    // flags that indicate that a custom selection column is present
    @Input() leftListHasSelectionColumn = false;
    @Input() rightListHasSelectionColumn = false;

    // flag to mark disabled items as always at the top when sorting
    @Input() disabledItemsFirst = false;

    // flags to mark single selection
    @Input() leftListIsSingleSelection = false;
    @Input() rightListIsSingleSelection = false;

    @Input() isMetadataViewFields = false;

    // emitter to know when data was changed for form dirty marker
    @Output() itemsMoved = new EventEmitter<any>();

    // custom emitter for move event, if default move is not good enough
    @Output() customMoveItems = new EventEmitter<any>();

    // custom emitter after drag drop event,
    @Output() customOnDropItems = new EventEmitter<any>();

    @Output() customItemSelection = new EventEmitter<any>();

    // disabled rows if the left-right-selection-box has only view permissions
    @Input() hasViewOnlyPermissions = false;

    @Input() isBusyLeft = false;
    @Input() isBusyMessageLeft: string;
    @Input() isBusyRight = false;
    @Input() isBusyMessageRight: string;

    initialItemLeftList: Array<DragDropData>;

    itemsLeftSelected: Array<any>;

    rowClassRules = {
        'disabled-row': (params) => {
            return (params.data && params.data.isDisabled && !params.data.isEmptyDropRow) || this.hasViewOnlyPermissions || params?.data?.deletePending;
        },
        'disabled-cells': (params) => {
            return (params.data && params.data.isDisabled && params.data.isEmptyDropRow);
        }
    };

    leftGridRowClassRules = {
        'disabled-row': (params) => {
            return this.hasViewOnlyPermissions || params?.data?.deletePending;
        }
    };

    get isDirty(): boolean {
        if (this.leftRightSelectionBoxForm) {
            return this.leftRightSelectionBoxForm.dirty;
        }
        return false;
    }
    set isDirty(value: boolean) {
        if (this.leftRightSelectionBoxForm && this.leftRightSelectionBoxForm.control) {
            if (value) {
                this.leftRightSelectionBoxForm.control.markAsDirty();
            } else {
                this.leftRightSelectionBoxForm.control.markAsPristine();
            }
        }
    }


    leftListOnSelectionChanged = (event?: SelectionChangedEvent) => {
        if (this.genericGrids.first.allRowsSelected || (this.genericGrids.first.selectedRows.length > 0 && this.genericGrids.last.selectedRows.length > 0)) {
            this.genericGrids.last.gridApi?.deselectAll();
        }
        this.checkSelectedNodesLeft(event);
    }

    checkSelectedNodesLeft = (event?: SelectionChangedEvent) => {
        if (this.leftListIsSingleSelection && this.genericGrids.first.selectedRows.length > 1) {
            const node = (<any>this.genericGrids.first.gridApi).selectionController.lastSelectedNode;
            this.genericGrids.first.gridApi?.deselectAll();
            this.genericGrids.first.selectNode(node);
        }

        this.itemsLeftSelected = this.genericGrids.first.selectedRows.map(a => Object.assign({}, a));

		if (this.customItemSelection.observers.length > 0) {
			this.customItemSelection.emit({ type: 'left', items: this.genericGrids.first.selectedRows, event: event });
		}
	}

    rightListOnSelectionChanged = (event?: SelectionChangedEvent) => {
        if (this.genericGrids.last.allRowsSelected || (this.genericGrids.first.selectedRows.length > 0 && this.genericGrids.last.selectedRows.length > 0)) {
            this.genericGrids.first.gridApi?.deselectAll();
        }
        this.checkSelectedNodesRight();

        if (this.customItemSelection.observers.length > 0) {
			this.customItemSelection.emit({ type: 'right', items: this.genericGrids.last.selectedRows, event: event });
        }
    }

    checkSelectedNodesRight = () => {
        if (this.rightListIsSingleSelection && this.genericGrids.last.selectedRows.length > 1) {
            const node = (<any>this.genericGrids.last.gridApi)?.selectionController.lastSelectedNode;
            this.genericGrids.last.gridApi?.deselectAll();
            this.genericGrids.last.selectNode(node);
        }
    }

    constructor() {
        this.rightListRequired = false;
        this.leftListRequired = false;
    }

    ngOnInit(): void {
        this.isDirty = false;
        if (this.leftListTitle) {
            const id = this.leftListTitle.replace(/[^a-zA-Z0-9-_ ]/gi, '');
            if (id) {
                this.automationIdLeft = this.automationId + `-${id.replace(/([a-z])([A-Z])/g, '$1-$2').replace(/\s+/g, '-').toLowerCase()}`;
            }
        }
        if (this.rightListTitle) {
            let id = this.rightListTitle.replace(/[^a-zA-Z0-9-_ ]/gi, '');
            if (id) {
                id = id === 'Selected Field' ? 'Selected Fields' : id;
                this.automationIdRight = this.automationId + `-${id.replace(/([a-z])([A-Z])/g, '$1-$2').replace(/\s+/g, '-').toLowerCase()}`;
            }
        }
    }

    setLeftItems(items: Array<DragDropData>): void {
        this.itemsLeft = items;
        if (!this.useTrees) {
            this.genericGrids.first.setData(this.itemsLeft);
        } else {
            this.genericTrees.first.setData(this.itemsLeft);
        }
    }

    setRightItems(items: Array<DragDropData>): void {
        this.itemsRight = items;
        if (!this.useTrees) {
            if (this.useDragAndDrop && (!this.leftListIsSingleSelection || this.itemsRight.length === 0)) {
                this.genericGrids.last.setData(this.itemsRight.concat(new DragDropData({ isDisabled: true, isEmptyDropRow: true })));
            } else {
                this.genericGrids.last.setData(this.itemsRight);
            }
        } else {
            this.genericTrees.last.setData(this.itemsRight);
        }
    }

    buttonMode(): string {
        if (this.useTrees) {
            if (this.genericTrees && this.genericTrees.first && this.genericTrees.first.selectedNodes.length > 0) {
                return 'add';
            }
            if (this.genericTrees && this.genericTrees.last && this.genericTrees.last.selectedNodes.length > 0) {
                return 'remove';
            }
        } else {
            if (this.genericGrids && this.genericGrids.first && this.genericGrids.first.selectedRows.length > 0) {
                return 'add';
            }
            if (this.genericGrids && this.genericGrids.last && this.genericGrids.last.selectedRows.length > 0) {
                return 'remove';
            }
        }
        return null;
    }

    buttonId(): string {
        if (this.buttonMode() === 'add') {
            return this.automationId + '-right-arrow-button';
        } else if (this.buttonMode() === 'remove') {
            return this.automationId + '-left-arrow-button';
        }
        return this.automationId + '-disabled-arrow-button';
    }

    moveItems(): void {
        let activeItems: Array<any>;
        if (this.useTrees) {
            activeItems = this.buttonMode() === 'add' ? this.genericTrees.first.selectedNodes : this.genericTrees.last.selectedNodes;
        } else {
            activeItems = this.buttonMode() === 'add' ? this.genericGrids.first.selectedRows : this.genericGrids.last.selectedRows;
            // get active items for lists
        }
        if (activeItems) {
            if (this.customMoveItems.observers.length > 0) {
                this.customMoveItems.emit({ mode: this.buttonMode(), items: activeItems });
            } else {
                this.moveActiveItems(activeItems);
            }
            if (this.itemsMoved.observers.length > 0) {
                this.itemsMoved.emit();
            }
            this.isDirty = true;
        }
    }

    sortList(list: Array<any>): void {
        if (list) {
            list.sort((a, b) => {
                if (this.disabledItemsFirst && a.isDisabled) {
                    return -1;
                }
                if (this.disabledItemsFirst && b.isDisabled) {
                    return 1;
                }
                const nameA = a.name ? a.name.toLowerCase() : '';
                const nameB = b.name ? b.name.toLowerCase() : '';
                if (nameA < nameB) {
                    return -1;
                }
                if (nameA > nameB) {
                    return 1;
                }
                return 0;
            });
        }
    }

    onCustomFilterCallbackLeftGrid = (event?: SelectionChangedEvent) => {
        this.setLeftItems(this.itemsLeft);
        if (this.genericGrids.first.gridCheckedItems.length > 0) {
            this.genericGrids.first.gridApi?.forEachNode((node) => {
                this.genericGrids.first.gridCheckedItems.forEach((entry) => {
                    if (_.isEqual(node.data, entry)) {
                        node.setSelected(true);
                    }
                });
            });
        }
    }

    checkboxSelectionChangedCallbackLeft = (selectedNodes: Array<GenericTreeNode>, node: GenericTreeNode) => {
        if (selectedNodes && node) {
            this.genericTrees.last.deselectAllNodes();
        }
    }

    checkboxSelectionChangedCallbackRight = (selectedNodes: Array<GenericTreeNode>, node: GenericTreeNode) => {
        if (selectedNodes && node) {
            this.genericTrees.first.deselectAllNodes();
        }
    }

    checkPartiallySelected(isLeftSide) {
        if (isLeftSide) {
            return this.genericTrees?.first?.childrenPartiallySelected(null);
        } else {
            return this.genericTrees?.last?.childrenPartiallySelected(null);
        }
    }

     checkAllSelected(isLeftSide) {
        if (isLeftSide) {
            return this.genericTrees?.first?.childrenAllSelected(null);
        } else {
            return this.genericTrees?.last?.childrenAllSelected(null);
        }
    }

    moveActiveItems(activeItems: Array<any>): void {
        activeItems.forEach(item => {
            const rightList = this.useTrees ? this.itemsRight[0].children : this.itemsRight;
            let sourceArray = this.buttonMode() === 'add' ? this.itemsLeft : rightList;
            let targetArray = this.buttonMode() === 'add' ? rightList : this.itemsLeft;
            if (this.useTrees && item instanceof GenericTreeNode) {
                if (item.data && item.data.parentName) {
                    const sourceParent = sourceArray.filter(n => n.name === item.data.parentName)[0] as GenericTreeNode;
                    if (sourceParent) {
                        sourceArray = sourceParent.children;
                    }
                    const targetParent = targetArray.filter(n => n.name === item.data.parentName)[0] as GenericTreeNode;
                    if (targetParent) {
                        targetArray = targetParent.children;
                    }
                }
                sourceArray.splice(sourceArray.indexOf(item), 1);
                const newTreeItem: GenericTreeNode = new GenericTreeNode(item);
                targetArray.push(newTreeItem);
            } else if (item instanceof DragDropData) {
                const newItem: DragDropData = sourceArray.splice(sourceArray.indexOf(item), 1).pop();
                if (newItem) {
                    targetArray.push(newItem);
                }
            }
            if ((this.buttonMode() === 'add' && this.sortItemsRight) || this.sortItemsLeft) {
                this.sortList(targetArray);
            }
        });
        this.setLeftItems(this.itemsLeft);
        this.setRightItems(this.itemsRight);
    }

    onDropLeft(dragData: DragDropData, dropData?: DragDropData, deleteSingleItem?: boolean, filterQuery?: string) {
        if (dragData) {
            const selectedItems = this.genericGrids.last.selectedRows.length > 1 && !deleteSingleItem ?
                this.genericGrids.last.selectedRows.reverse() : [dragData];
            this.moveDraggedItems(selectedItems, this.itemsRight, this.initialItemLeftList, this.initialItemLeftList.indexOf(dropData), filterQuery);

            if (this.customOnDropItems.observers.length > 0) {
                this.customOnDropItems.emit({ type: 'left', items: selectedItems });
            }
        }
    }

    onDropRight(dragData: DragDropData, dropData: DragDropData, filterQuery?: string, mode?: string) {
        let selectedItems = null;
        if (dragData && dropData) {
            const index = this.itemsRight.findIndex(ir => ir.name === dropData.name);
            if (this.itemsRight.find(i => i.name === dragData.name)) {
                selectedItems = this.genericGrids.last.selectedRows.length > 1 ?
                    this.genericGrids.last.selectedRows : [dragData];
                this.reorderItems(selectedItems, this.itemsRight, index);
            } else {
                if (mode === GroupFieldMode.DUPLICATE_VALUES) {
                    this.replaceItem(dragData, this.initialItemLeftList, this.itemsRight);
                    selectedItems = [dragData];
                } else {
                    selectedItems = this.genericGrids.first.selectedRows.length > 1 ?
                        this.genericGrids.first.selectedRows : [dragData];
                    if (!mode) {
                        selectedItems = _.sortBy(selectedItems, [function (item) { return item.name; }]);
                    }
                    this.moveDraggedItems(selectedItems, this.initialItemLeftList, this.itemsRight, index, filterQuery);
                }
            }

            if (this.customOnDropItems.observers.length > 0) {
                this.customOnDropItems.emit({ type: 'right', items: selectedItems });
            }
        }
    }

    moveDraggedItems(selectedItems: Array<DragDropData>, fromArray: Array<DragDropData>, toArray: Array<DragDropData>, atIndex: number, filterQuery?: string) {
        if (selectedItems && fromArray && toArray) {
            if (!isNumber(atIndex) || atIndex === -1) {
                atIndex = toArray.length;
            }
            selectedItems.reverse();
            selectedItems.forEach(item => {
                const idxOfItem = fromArray.findIndex(entry => item.name === entry.name);
                const newItem: DragDropData = fromArray.splice(idxOfItem, 1).pop();
                if (newItem) {
                    toArray.splice(atIndex, 0, newItem);
                }
            });
            this.genericGrids.first.allRowsSelected = false;
            this.setLeftItems(this.initialItemLeftList);
            this.setRightItems(this.itemsRight);
        }
    }

    reorderItems(selectedItems: Array<DragDropData>, toArray: Array<DragDropData>, atIndex: number) {
        if (selectedItems && toArray) {
            if (atIndex === -1) {
                atIndex = toArray.length;
            }
            let idxOfMovedItem = -1;
            if (selectedItems.length === 1) {
                idxOfMovedItem = toArray.findIndex(e => e.name === selectedItems[0].name);
                const newItem = _lo.cloneDeep(selectedItems[0]);
                toArray.splice(atIndex, 0, newItem);
                if (idxOfMovedItem >= 0) {
                    toArray.splice(idxOfMovedItem > atIndex ? idxOfMovedItem + 1 : idxOfMovedItem, 1);
                }
            } else {
                let isFirstItem = true;
                selectedItems.forEach(item => {
                    const oldIdx = toArray.findIndex(e => e.name === item.name);
                    const newItem = _lo.cloneDeep(item);
                    if (!isFirstItem) {
                        atIndex++;
                    }
                    toArray.splice(atIndex, 0, newItem);
                    if (oldIdx >= 0) {
                        toArray.splice(oldIdx > atIndex ? oldIdx + 1 : oldIdx, 1);
                    }
                    if (isFirstItem) {
                        isFirstItem = false;
                    }
                });
            }

            this.setLeftItems(this.itemsLeft);
            this.setRightItems(this.itemsRight);
        }
    }

    replaceItem(dragData: DragDropData, fromArray: Array<DragDropData>, toArray: Array<DragDropData>) {
        if (dragData && fromArray && toArray) {
            const newItem: DragDropData = fromArray.splice(fromArray.findIndex(a => a.name === dragData.name), 1).pop();
            const remItems = toArray.splice(0, toArray.length, newItem);
            fromArray.splice(0, 0, ...remItems);
            this.setLeftItems(this.itemsLeft);
            this.setRightItems(this.itemsRight);
        }
    }

    hasMetadataViewFieldsPermission(): boolean {
        return this.isMetadataViewFields && this.hasViewOnlyPermissions;
    }

    isMetadataViewFieldsRefined(): string {
        return this.hasMetadataViewFieldsPermission() ? 'metadata-view-fields-refinement' : '';
    }
}
